import { useEffect, useState, Fragment } from 'react';
import { api } from '../lib/api';
import { Users, RefreshCw, Loader2, Search, CalendarClock, ClipboardPaste, Zap, Download, Trash2,
         ChevronRight, ChevronDown, ShieldCheck, PlayCircle, UserPlus, Gift, MessageSquare } from 'lucide-react';

function coerceError(e, fallback) {
  const d = e?.response?.data?.detail;
  if (typeof d === 'string') return d;
  if (Array.isArray(d) && d.length) {
    return d.map(x => (typeof x === 'string' ? x : (x?.msg || JSON.stringify(x)))).join('; ');
  }
  if (d && typeof d === 'object') return d.msg || JSON.stringify(d);
  return e?.message || fallback;
}

function relTime(iso) {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  if (isNaN(then)) return '';
  const secs = Math.max(0, Math.round((Date.now() - then) / 1000));
  if (secs < 60) return 'just now';
  const mins = Math.round(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.round(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(iso).toLocaleDateString();
}

// Iter-52.28 — Exact timestamp formatter. "22 Sep, 2:15 PM" — short
// month + day, then hh:mm AM/PM in the admin's locale. Beats a bare
// ISO string for scannability and lets Leena cross-reference exact
// signup times against Facebook Ads Manager without maths.
function exactTime(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  const datePart = d.toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
  const timePart = d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  return `${datePart}, ${timePart}`;
}

function usageCls(label) {
  if (label === 'Active') return 'bg-emerald-100 text-emerald-800';
  if (label === 'Low activity') return 'bg-amber-100 text-amber-800';
  // Iter-52.37 — brand-new signup (<48h, no reading yet). Neutral
  // blue so admins don't confuse it with the red-flag "Inactive"
  // chip; nothing to worry about yet, just newly onboarded.
  if (label === 'New') return 'bg-sky-100 text-sky-800';
  return 'bg-slate-100 text-slate-600';
}

// Iter-51.94 — Plan chip color per tier so admins can eyeball who's on
// which subscription level. Trial gets a neutral slate so it doesn't
// visually compete with the paid tiers.
const PLAN_TIER_CHIP = {
  trial:    'bg-slate-200 text-slate-700',
  starter:  'bg-sky-100 text-sky-800',
  growth:   'bg-emerald-100 text-emerald-800',
  standard: 'bg-violet-100 text-violet-800',
  premium:  'bg-amber-100 text-amber-900',
};

function planChipParts(row) {
  // Trial accounts have `status === 'trial'` and typically no explicit
  // `plan_id` (backend maps them to Starter limits but the account has
  // not subscribed yet). Show "TRIAL" so admins never confuse them
  // with a paid Starter.
  if (row?.status === 'trial') {
    return { tier: 'trial', label: 'TRIAL', cycle: '' };
  }
  const tier = (row?.plan_id || '').toLowerCase() || 'starter';
  const label = (row?.plan_name || tier || 'Starter').toString().toUpperCase();
  const cycleRaw = (row?.plan || '').toString().toLowerCase();
  const cycle = cycleRaw === 'annual' ? 'Annual'
               : cycleRaw === 'monthly' ? 'Monthly'
               : '';
  return { tier, label, cycle };
}

// Iter-51.65 — visual chip for the derived watchdog_status. Same
// palette as the status column so admins can scan both columns
// without re-learning colors.
const WATCHDOG_CHIP = {
  exempt:    { cls: 'bg-slate-200 text-slate-600',       label: 'exempt' },
  clear:     { cls: 'bg-emerald-100 text-emerald-800',   label: 'clear' },
  over_limit:{ cls: 'bg-orange-100 text-orange-800',     label: 'over limit' },
  waiting:   { cls: 'bg-rose-100 text-rose-800',         label: 'waiting delete' },
  flagged:   { cls: 'bg-amber-100 text-amber-800',       label: 'manual review' },
};

function fmtDateShort(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

function formatInr(n) {
  const v = Number(n) || 0;
  return '₹' + v.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/**
 * AdminUsersCard — Iter-51.63 light-theme + per-child expand + Case B flag filter.
 */
export default function AdminUsersCard({ debugPin }) {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [q, setQ] = useState('');
  const [onlyFlagged, setOnlyFlagged] = useState(false);
  // Iter-52.35/36 — zero-activity filter state.
  //   • zeroPeriod: 'off' | 'today' | 'yesterday' | '2d_before'
  //                 | '3d_before' | '7d' | '15d' | 'custom'
  //   • zeroFrom/zeroTo: only used when zeroPeriod==='custom'
  //   • includeLow: widens qualification from 0 to <=2 activity in window.
  //   • zeroWindow: server-echoed window ({from,to,include_low_activity})
  //                 so we can render a persistent header telling admins
  //                 which slice of time they're viewing.
  const [zeroPeriod, setZeroPeriod] = useState('off');
  const [zeroFrom, setZeroFrom] = useState('');
  const [zeroTo, setZeroTo] = useState('');
  const [includeLow, setIncludeLow] = useState(false);
  const [zeroWindow, setZeroWindow] = useState(null);
  // Iter-52.40 — Registration-date and Child-added-date filters.
  // Same period-selector shape as Zero-activity (Today / Yesterday /
  // 2d / 3d / 7d / 15d / Custom). 'off' means the filter is disabled.
  const [regPeriod, setRegPeriod] = useState('off');
  const [regFrom, setRegFrom] = useState('');
  const [regTo, setRegTo] = useState('');
  const [childPeriod, setChildPeriod] = useState('off');
  const [childFrom, setChildFrom] = useState('');
  const [childTo, setChildTo] = useState('');
  // Iter-52.46 — trial-status filter. 'all' means no filter applied.
  const [trialStatus, setTrialStatus] = useState('all');
  // Iter-52.47 — extra-profile-request filter state (same shape as
  // the child-added filter).
  const [extraPeriod, setExtraPeriod] = useState('off');
  const [extraFrom, setExtraFrom] = useState('');
  const [extraTo, setExtraTo] = useState('');
  // Iter-52.48 — "Gifted but not used yet" filter. Simple boolean —
  // narrows the list to parents who were granted an extra_profile_bonus
  // but haven't yet added a second child to fill the slot.
  const [giftUnusedOnly, setGiftUnusedOnly] = useState(false);
  const [expanded, setExpanded] = useState({}); // {user_id: bool}
  const [editing, setEditing] = useState(null);
  const [newDate, setNewDate] = useState('');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [activating, setActivating] = useState(null);
  const [actPlan, setActPlan] = useState('monthly');
  const [actTier, setActTier] = useState('starter');
  const [actStart, setActStart] = useState('');
  const [actNote, setActNote] = useState('');
  const [actSaving, setActSaving] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [delNote, setDelNote] = useState('');
  const [delConfirmText, setDelConfirmText] = useState('');
  const [delSaving, setDelSaving] = useState(false);
  const [flash, setFlash] = useState('');
  const [runningWatchdog, setRunningWatchdog] = useState(false);
  // Iter-52.20 — profile-bonus modal state. `bonusFor` is the user
  // row we're editing; `bonusDelta` is signed (typical: +1 to gift,
  // -1 to revoke); `bonusNote` is an audit trail memo.
  const [bonusFor, setBonusFor] = useState(null);
  const [bonusDelta, setBonusDelta] = useState(1);
  const [bonusNote, setBonusNote] = useState('');
  const [bonusSaving, setBonusSaving] = useState(false);

  // Iter-52.40 — resolve one of the shared period presets into a
  // (from, to) tuple of YYYY-MM-DD strings. Kept client-side so the
  // three period-filters (zero-activity, registered-on, child-added)
  // can share this pattern without every one plumbing its own math.
  // Custom just returns whatever the caller supplies.
  const resolvePeriodDates = (period, customFrom, customTo) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    // Iter-52.42 — emit the LOCAL (IST for admins in India) calendar
    // date instead of the UTC one. Prior implementation used
    // `d.toISOString().slice(0,10)` which converts local → UTC first,
    // so IST midnight Sep 22 became UTC 18:30 Sep 21 and the client
    // sent "2026-09-21" when the admin meant "today". The backend
    // now treats the received YYYY-MM-DD as an IST calendar date
    // and expands it into the correct UTC ISO range on the server.
    const iso = (d) => {
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${y}-${m}-${day}`;
    };
    const daysAgo = (n) => { const d = new Date(today); d.setDate(d.getDate() - n); return d; };
    switch (period) {
      case 'today':      return { from: iso(today), to: iso(today) };
      case 'yesterday':  return { from: iso(daysAgo(1)), to: iso(daysAgo(1)) };
      case '2d_before':  return { from: iso(daysAgo(2)), to: iso(daysAgo(2)) };
      case '3d_before':  return { from: iso(daysAgo(3)), to: iso(daysAgo(3)) };
      case '7d':         return { from: iso(daysAgo(6)), to: iso(today) };
      case '15d':        return { from: iso(daysAgo(14)), to: iso(today) };
      case 'custom':     return { from: customFrom || '', to: customTo || '' };
      default:           return null;
    }
  };

  const load = async (query = q, flagged = onlyFlagged, zeroOpts = null, regOpts = null, childOpts = null, trialOpts = null, extraOpts = null, giftOpts = null) => {
    if (!debugPin) return;
    setBusy(true); setErr('');
    // Iter-52.35/36 — resolve the zero-activity params.
    const period = (zeroOpts && zeroOpts.period !== undefined) ? zeroOpts.period : zeroPeriod;
    const from = (zeroOpts && zeroOpts.from !== undefined) ? zeroOpts.from : zeroFrom;
    const to = (zeroOpts && zeroOpts.to !== undefined) ? zeroOpts.to : zeroTo;
    const low = (zeroOpts && zeroOpts.low !== undefined) ? zeroOpts.low : includeLow;
    const zeroPayload = period !== 'off' ? {
      only_zero_activity: true,
      zero_activity_period: period,
      include_low_activity: !!low,
      ...(period === 'custom' ? { zero_activity_from: from, zero_activity_to: to } : {}),
    } : {};

    // Iter-52.40 — resolve registration-date + child-added-date params.
    // Both use the same preset shape; we translate to (from, to) ISO
    // dates client-side and send as `registered_from/to` etc so the
    // backend range logic can just consume them without knowing the
    // preset names.
    const rp = (regOpts && regOpts.period !== undefined) ? regOpts.period : regPeriod;
    const rf = (regOpts && regOpts.from !== undefined) ? regOpts.from : regFrom;
    const rt = (regOpts && regOpts.to !== undefined) ? regOpts.to : regTo;
    let regPayload = {};
    if (rp !== 'off') {
      const dates = resolvePeriodDates(rp, rf, rt) || {};
      if (dates.from && dates.to) regPayload = { registered_from: dates.from, registered_to: dates.to };
    }

    const cp = (childOpts && childOpts.period !== undefined) ? childOpts.period : childPeriod;
    const cf = (childOpts && childOpts.from !== undefined) ? childOpts.from : childFrom;
    const ct = (childOpts && childOpts.to !== undefined) ? childOpts.to : childTo;
    let childPayload = {};
    if (cp !== 'off') {
      const dates = resolvePeriodDates(cp, cf, ct) || {};
      if (dates.from && dates.to) childPayload = { child_added_from: dates.from, child_added_to: dates.to };
    }

    try {
      // Iter-52.46 — trial-status filter payload.
      const ts = (trialOpts && trialOpts.status !== undefined) ? trialOpts.status : trialStatus;
      const trialPayload = (ts && ts !== 'all') ? { trial_status: ts } : {};
      // Iter-52.47 — extra-profile-request filter payload.
      const ep = (extraOpts && extraOpts.period !== undefined) ? extraOpts.period : extraPeriod;
      const ef = (extraOpts && extraOpts.from !== undefined) ? extraOpts.from : extraFrom;
      const et = (extraOpts && extraOpts.to !== undefined) ? extraOpts.to : extraTo;
      let extraPayload = {};
      if (ep !== 'off') {
        const dates = resolvePeriodDates(ep, ef, et) || {};
        if (dates.from && dates.to) extraPayload = { extra_profile_from: dates.from, extra_profile_to: dates.to };
      }
      // Iter-52.48 — Gifted-but-not-used filter payload. Backend
      // reads `gift_unused: true` and narrows to the qualifying set.
      const gu = (giftOpts && giftOpts.on !== undefined) ? giftOpts.on : giftUnusedOnly;
      const giftPayload = gu ? { gift_unused: true } : {};
      const { data } = await api.post('/admin/users', {
        pin: debugPin, limit: 50, query, only_flagged: flagged,
        ...zeroPayload, ...regPayload, ...childPayload, ...trialPayload, ...extraPayload, ...giftPayload,
      });
      setRows(data.users || []);
      setTotal(data.total || 0);
      setZeroWindow(data.zero_activity_window || null);
    } catch (e) {
      setErr(coerceError(e, 'Failed to load users.'));
    } finally { setBusy(false); }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load('', false); }, []);

  const runWatchdog = async () => {
    if (!debugPin) return;
    setRunningWatchdog(true); setErr('');
    try {
      const { data } = await api.post('/admin/over-profile/run-once', { pin: debugPin });
      const s = data.stats || {};
      // Iter-51.65 — surface EVERY bucket so the numbers always add
      // up (`scanned = warned + deleted + flagged + waiting + cleared
      // + skipped`). Prior version hid `cleared` + `waiting`, which
      // made real accounts appear to vanish.
      setFlash(
        `Watchdog: ${s.scanned || 0} scanned · ${s.warned || 0} warned · ` +
        `${s.deleted || 0} deleted · ${s.manual_review || 0} flagged · ` +
        `${s.waiting || 0} waiting · ${s.cleared || 0} clear · ` +
        `${s.skipped || 0} exempt`
      );
      await load(q, onlyFlagged);
    } catch (e) { setErr(coerceError(e, 'Watchdog run failed.')); }
    finally { setRunningWatchdog(false); }
  };

  const clearFlag = async (row) => {
    if (!window.confirm(`Clear the manual-review flag for ${row.email}? Do this only after you've resolved the extra-profile situation with them.`)) return;
    setErr('');
    try {
      await api.post('/admin/over-profile/clear-flag', { pin: debugPin, email: row.email });
      setFlash(`Flag cleared for ${row.email}.`);
      await load(q, onlyFlagged);
    } catch (e) { setErr(coerceError(e, 'Failed to clear flag.')); }
  };

  const openExtend = (row) => {
    setEditing(row);
    const d = new Date(); d.setDate(d.getDate() + 30);
    setNewDate(d.toISOString().slice(0, 10));
    setNote('');
  };

  // Iter-52.20 — profile bonus modal helpers. Same PIN, same audit
  // trail as extend/activate. Delta defaults to +1 (gift one profile);
  // admin can flip to -1 to revoke.
  const openBonus = (row) => {
    setBonusFor(row);
    setBonusDelta(1);
    setBonusNote('');
  };
  const saveBonus = async () => {
    if (!bonusFor || !bonusDelta) return;
    setBonusSaving(true); setErr(''); setFlash('');
    try {
      // Iter-52.45 — send user_id + email + mobile_e164 so the backend
      // can resolve the row for phone-only signups (email may be null).
      const { data } = await api.post('/admin/users/profile-bonus', {
        pin: debugPin,
        user_id: bonusFor.user_id,
        email: bonusFor.email || undefined,
        mobile_e164: bonusFor.mobile_e164 || undefined,
        delta: parseInt(bonusDelta, 10),
        note: bonusNote,
      });
      const who = bonusFor.email || bonusFor.mobile_e164 || bonusFor.user_id;
      setFlash(
        `Profile limit for ${who} is now ${data.effective_profile_limit} `
        + `(bonus ${data.new_bonus >= 0 ? '+' : ''}${data.new_bonus}).`
      );
      await load();
      setTimeout(() => { setBonusFor(null); setFlash(''); }, 1800);
    } catch (e) {
      setErr(coerceError(e, 'Failed to update profile bonus.'));
    } finally { setBonusSaving(false); }
  };

  const save = async () => {
    if (!editing || !newDate) return;
    setSaving(true); setErr(''); setFlash('');
    try {
      const { data } = await api.post('/admin/users/extend', {
        pin: debugPin, email: editing.email, new_expires_at: newDate, note,
      });
      const until = data?.access_expires_at
        ? new Date(data.access_expires_at).toLocaleDateString()
        : newDate;
      setFlash(`Trial extended for ${editing.email} until ${until}.`);
      await load();
      // Iter-51.72 — leave the modal open for 1.5s so the admin
      // sees the inline ✓ success message before it disappears.
      setTimeout(() => { setEditing(null); setFlash(''); }, 1500);
    } catch (e) {
      setErr(coerceError(e, 'Failed to extend.'));
    } finally { setSaving(false); }
  };

  const openActivate = (row) => {
    setActivating(row);
    setActPlan('monthly');
    setActTier(row.plan_id || 'starter');
    setActStart(new Date().toISOString().slice(0, 10));
    setActNote('');
  };

  const saveActivate = async () => {
    if (!activating) return;
    setActSaving(true); setErr(''); setFlash('');
    try {
      const { data } = await api.post('/admin/subscription/activate', {
        pin: debugPin, email: activating.email, plan: actPlan,
        plan_id: actTier,
        starts_at: actStart || undefined, note: actNote,
      });
      const until = data?.access_expires_at
        ? new Date(data.access_expires_at).toLocaleDateString()
        : '';
      setFlash(`Plan activated for ${activating.email} — ${actTier}${until ? ` — access until ${until}.` : '.'}`);
      await load();
      // Iter-51.72 — leave the modal open for 1.5s so the admin
      // sees the inline ✓ success message before it disappears.
      setTimeout(() => { setActivating(null); setFlash(''); }, 1500);
    } catch (e) {
      setErr(coerceError(e, 'Failed to activate.'));
    } finally { setActSaving(false); }
  };

  const openDelete = (row) => {
    setDeleting(row);
    setDelNote('');
    setDelConfirmText('');
  };

  const saveDelete = async () => {
    if (!deleting) return;
    if (delConfirmText.trim().toUpperCase() !== 'DELETE') return;
    setDelSaving(true); setErr('');
    try {
      // Iter-52.24 — always send user_id (guaranteed on every row) so
      // deletion works even if the row's email/mobile happen to be
      // empty or fell off a stale bundle. email/mobile still sent for
      // audit-friendliness in the response payload.
      const { data } = await api.post('/admin/users/delete', {
        pin: debugPin,
        user_id: deleting.user_id,
        email: deleting.email || null,
        mobile_e164: deleting.mobile_e164 || null,
        confirm: true,
        note: delNote,
      });
      const kids = data?.child_ids_removed ?? 0;
      const sessions = data?.deleted?.sessions ?? 0;
      const runs = data?.deleted?.quiz_runs ?? 0;
      const label = deleting.email || deleting.mobile_e164 || deleting.user_id;
      setFlash(
        `Account deleted: ${label} · ${kids} profile${kids === 1 ? '' : 's'}, ` +
        `${sessions} reading session${sessions === 1 ? '' : 's'}, ` +
        `${runs} quiz run${runs === 1 ? '' : 's'} removed.`
      );
      // Iter-52.24 — optimistically drop the row from local state so
      // the UI reflects the delete even if the follow-up list refresh
      // fails or takes a moment. `load()` still runs to reconcile.
      setRows((prev) => prev.filter((row) => row.user_id !== deleting.user_id));
      setDeleting(null);
      await load();
    } catch (e) {
      setErr(coerceError(e, 'Failed to delete account.'));
    } finally { setDelSaving(false); }
  };

  const chipCls = (s) => s === 'active'
    ? 'bg-emerald-100 text-emerald-800'
    : s === 'trial'
    ? 'bg-sky-100 text-sky-800'
    : 'bg-rose-100 text-rose-800';

  const downloadCsv = () => {
    const base = process.env.REACT_APP_BACKEND_URL || '';
    const url = `${base}/api/admin/users/export.csv?pin=${encodeURIComponent(debugPin || '')}`;
    const a = document.createElement('a');
    a.href = url; a.rel = 'noopener noreferrer'; a.target = '_blank';
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
  };

  const flaggedCount = rows.filter(r => r.over_profile_needs_manual_review).length;

  return (
    <div data-testid="admin-users-card" className="mt-3 bg-white border border-slate-200 shadow-sm text-slate-800 rounded-2xl p-4 text-[13px]">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="text-slate-900 font-bold uppercase tracking-wider text-[13px] flex items-center gap-2">
          <Users size={16}/> User management
          <span className="text-slate-500 font-normal normal-case tracking-normal">({total})</span>
          {flaggedCount > 0 && (
            <span className="text-amber-800 bg-amber-100 border border-amber-300 rounded-full px-2 py-0.5 text-[11px] font-bold normal-case tracking-normal">
              {flaggedCount} needs review
            </span>
          )}
        </div>
        <div className="flex items-center gap-1 flex-wrap">
          <button data-testid="admin-users-run-watchdog" onClick={runWatchdog} disabled={runningWatchdog || busy}
            title="Run over-profile watchdog now"
            className="text-[12px] font-bold px-2.5 py-1 rounded-full bg-amber-600 text-white hover:bg-amber-700 inline-flex items-center gap-1 disabled:opacity-60">
            {runningWatchdog ? <Loader2 size={12} className="animate-spin"/> : <PlayCircle size={12}/>}
            Watchdog
          </button>
          <button data-testid="admin-users-export-csv" onClick={downloadCsv} disabled={busy || !debugPin}
            title="Download all users as an Excel-friendly CSV"
            className="text-[12px] font-bold px-2.5 py-1 rounded-full bg-emerald-600 text-white hover:bg-emerald-700 inline-flex items-center gap-1 disabled:opacity-60">
            <Download size={12}/> CSV
          </button>
          <button data-testid="admin-users-refresh" onClick={() => load()} disabled={busy}
            className="text-[12px] font-bold px-2.5 py-1 rounded-full bg-slate-200 text-slate-800 hover:bg-slate-300 inline-flex items-center gap-1 disabled:opacity-60">
            {busy ? <Loader2 size={12} className="animate-spin"/> : <RefreshCw size={12}/>}
            Refresh
          </button>
        </div>
      </div>

      <div className="flex items-center gap-2 mb-2 flex-wrap">
        <label className="flex items-center gap-2 bg-slate-100 rounded-lg px-2 py-1 flex-1 min-w-[220px]">
          <Search size={13} className="text-slate-500"/>
          <input data-testid="admin-users-search" value={q} onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') load(q, onlyFlagged); }}
            placeholder="Search by email, name, or mobile — press Enter"
            className="flex-1 bg-transparent text-[13px] text-slate-800 outline-none py-1"/>
        </label>
        <label className="text-[12px] text-slate-700 flex items-center gap-1.5 cursor-pointer bg-slate-100 rounded-lg px-2 py-1.5"
          data-testid="admin-users-only-flagged-label">
          <input data-testid="admin-users-only-flagged" type="checkbox" checked={onlyFlagged}
            onChange={(e) => { setOnlyFlagged(e.target.checked); load(q, e.target.checked); }}/>
          Only flagged for manual review
        </label>
      </div>

      {/* Iter-52.40 — shared period-filter helper used by the three
          date filters below (Registered, Kid profile added, Zero-
          activity). Rendered inline so the visual pattern stays
          identical across all three but state is owned by the
          caller. */}
      {(() => null)()}

      {/* Iter-52.40 — Registration-date filter row. Same period-
          selector shape as Zero-activity. Behind a master "Registered"
          button so the row stays compact when unused. */}
      <PeriodFilterRow
        testIdPrefix="admin-users-registered"
        label="Registered"
        active={regPeriod !== 'off'}
        period={regPeriod}
        from={regFrom}
        to={regTo}
        onToggle={() => {
          const next = regPeriod === 'off' ? 'today' : 'off';
          setRegPeriod(next);
          load(q, onlyFlagged, null, { period: next }, null);
        }}
        onPreset={(p) => { setRegPeriod(p); load(q, onlyFlagged, null, { period: p }, null); }}
        onCustomFromChange={setRegFrom}
        onCustomToChange={setRegTo}
        onCustomApply={() => load(q, onlyFlagged, null, { period: 'custom', from: regFrom, to: regTo }, null)}
        busy={busy}
      />

      {/* Iter-52.40 — New-child-added filter row. Distinct from
          "Registered" so admins can see EXISTING parents who added
          a sibling profile today. */}
      <PeriodFilterRow
        testIdPrefix="admin-users-child-added"
        label="Kid profile added"
        active={childPeriod !== 'off'}
        period={childPeriod}
        from={childFrom}
        to={childTo}
        onToggle={() => {
          const next = childPeriod === 'off' ? 'today' : 'off';
          setChildPeriod(next);
          load(q, onlyFlagged, null, null, { period: next });
        }}
        onPreset={(p) => { setChildPeriod(p); load(q, onlyFlagged, null, null, { period: p }); }}
        onCustomFromChange={setChildFrom}
        onCustomToChange={setChildTo}
        onCustomApply={() => load(q, onlyFlagged, null, null, { period: 'custom', from: childFrom, to: childTo })}
        busy={busy}
      />

      {/* Iter-52.47 — Extra-profile-request filter row. Reuses the
          shared PeriodFilterRow — same pattern as Registered / Kid
          profile added / Zero-activity. */}
      <PeriodFilterRow
        testIdPrefix="admin-users-extra-profile"
        label="Extra-profile requested"
        active={extraPeriod !== 'off'}
        period={extraPeriod}
        from={extraFrom}
        to={extraTo}
        onToggle={() => {
          const next = extraPeriod === 'off' ? 'today' : 'off';
          setExtraPeriod(next);
          load(q, onlyFlagged, null, null, null, null, { period: next });
        }}
        onPreset={(p) => { setExtraPeriod(p); load(q, onlyFlagged, null, null, null, null, { period: p }); }}
        onCustomFromChange={setExtraFrom}
        onCustomToChange={setExtraTo}
        onCustomApply={() => load(q, onlyFlagged, null, null, null, null, { period: 'custom', from: extraFrom, to: extraTo })}
        busy={busy}
      />

      {/* Iter-52.48 — Gifted-but-not-used filter chip. Simple boolean
          toggle — narrows to parents whose admin-granted extra
          profile slot is still unclaimed, so admins can nudge them
          to add a second kid. Visual language matches the
          trial-status filter row so it doesn't feel bolted on. */}
      <div className="mb-2 flex items-center gap-2 flex-wrap" data-testid="admin-users-gift-unused-row">
        <span className="text-[12px] font-bold text-slate-700">Gifted profiles</span>
        <button
          data-testid="admin-users-gift-unused-toggle"
          onClick={() => {
            const next = !giftUnusedOnly;
            setGiftUnusedOnly(next);
            load(q, onlyFlagged, null, null, null, null, null, { on: next });
          }}
          title="Show only parents I've gifted an extra profile slot but who haven't yet added a second kid"
          className={`text-[11px] font-bold px-2.5 py-1 rounded-full inline-flex items-center gap-1 ${giftUnusedOnly ? 'bg-fuchsia-600 text-white hover:bg-fuchsia-700' : 'bg-slate-200 text-slate-800 hover:bg-slate-300'}`}
        >
          <Gift size={11}/> Gifted, not used yet
        </button>
      </div>

      {/* Iter-52.46 — Trial-status filter row. Simple segmented
          buttons — no date pickers needed, since it just narrows
          by the derived account status. Uses the same visual
          language as the other filter rows for consistency. */}
      <div className="mb-2 flex items-center gap-2 flex-wrap" data-testid="admin-users-trial-status-row">
        <span className="text-[12px] font-bold text-slate-700">Trial status</span>
        <div className="inline-flex bg-slate-100 rounded-full p-0.5" data-testid="admin-users-trial-status-group">
          {[
            ['all',     'All'],
            ['trial',   'On trial'],
            ['active',  'Paid / Active'],
            ['expired', 'Expired'],
          ].map(([key, label]) => (
            <button
              key={key}
              data-testid={`admin-users-trial-status-${key}`}
              onClick={() => { setTrialStatus(key); load(q, onlyFlagged, null, null, null, { status: key }); }}
              className={`text-[11px] font-bold px-2.5 py-1 rounded-full ${trialStatus === key ? 'bg-white text-indigo-700 shadow-sm' : 'text-slate-600 hover:text-slate-800'}`}
            >{label}</button>
          ))}
        </div>
      </div>

      {/* Iter-52.35/36 — Zero-activity filter row. Segmented period
          buttons + custom range + include-low toggle. Whole row is
          hidden behind a single "Zero-activity" master button so the
          admin doesn't see 8 date controls unless they need them. */}
      <div className="mb-2 flex items-center gap-2 flex-wrap" data-testid="admin-users-zero-row">
        <button
          data-testid="admin-users-zero-toggle"
          onClick={() => {
            const nextPeriod = zeroPeriod === 'off' ? '15d' : 'off';
            setZeroPeriod(nextPeriod);
            if (nextPeriod === 'off') setZeroWindow(null);
            load(q, onlyFlagged, { period: nextPeriod });
          }}
          className={`text-[12px] font-bold px-2.5 py-1 rounded-full inline-flex items-center gap-1 ${zeroPeriod !== 'off' ? 'bg-indigo-600 text-white hover:bg-indigo-700' : 'bg-slate-200 text-slate-800 hover:bg-slate-300'}`}
          title="Show users who haven't done any reading in the selected window"
        >
          Zero activity
        </button>
        {zeroPeriod !== 'off' && (
          <>
            <div className="inline-flex bg-slate-100 rounded-full p-0.5 flex-wrap" data-testid="admin-users-zero-period-group">
              {[
                ['today',     'Today'],
                ['yesterday', 'Yesterday'],
                ['2d_before', '2d ago'],
                ['3d_before', '3d ago'],
                ['7d',        '7 days'],
                ['15d',       '15 days'],
                ['custom',    'Custom'],
              ].map(([key, label]) => (
                <button
                  key={key}
                  data-testid={`admin-users-zero-period-${key}`}
                  onClick={() => { setZeroPeriod(key); load(q, onlyFlagged, { period: key }); }}
                  className={`text-[11px] font-bold px-2.5 py-1 rounded-full ${zeroPeriod === key ? 'bg-white text-indigo-700 shadow-sm' : 'text-slate-600 hover:text-slate-800'}`}
                >{label}</button>
              ))}
            </div>
            {zeroPeriod === 'custom' && (
              <div className="inline-flex items-center gap-1" data-testid="admin-users-zero-custom">
                <input
                  data-testid="admin-users-zero-from"
                  type="date"
                  value={zeroFrom}
                  onChange={(e) => setZeroFrom(e.target.value)}
                  className="text-[12px] bg-white border border-slate-300 rounded px-1.5 py-0.5"
                />
                <span className="text-slate-500 text-[12px]">to</span>
                <input
                  data-testid="admin-users-zero-to"
                  type="date"
                  value={zeroTo}
                  onChange={(e) => setZeroTo(e.target.value)}
                  className="text-[12px] bg-white border border-slate-300 rounded px-1.5 py-0.5"
                />
                <button
                  data-testid="admin-users-zero-custom-apply"
                  onClick={() => load(q, onlyFlagged, { period: 'custom', from: zeroFrom, to: zeroTo })}
                  disabled={!zeroFrom || !zeroTo || busy}
                  className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-60"
                >Apply</button>
              </div>
            )}
            <label
              className="text-[11px] text-slate-700 flex items-center gap-1 cursor-pointer bg-slate-100 rounded-full px-2 py-1"
              data-testid="admin-users-include-low-label"
            >
              <input
                data-testid="admin-users-include-low"
                type="checkbox"
                checked={includeLow}
                onChange={(e) => { setIncludeLow(e.target.checked); load(q, onlyFlagged, { low: e.target.checked }); }}
              />
              Include low-activity (1–2)
            </label>
            {zeroWindow && (
              <span
                data-testid="admin-users-zero-window-badge"
                className="text-[11px] font-bold px-2 py-1 rounded-full bg-indigo-50 text-indigo-800 border border-indigo-200"
              >
                {zeroWindow.from === zeroWindow.to
                  ? zeroWindow.from
                  : `${zeroWindow.from} → ${zeroWindow.to}`}
                {zeroWindow.include_low_activity ? ' · incl. low' : ''}
              </span>
            )}
          </>
        )}
      </div>

      {err && <div data-testid="admin-users-err" className="text-[13px] text-rose-600 mb-2">{err}</div>}
      {flash && (
        <div data-testid="admin-users-flash"
          className="text-[13px] text-emerald-800 bg-emerald-50 border border-emerald-300 rounded px-2 py-1 mb-2 flex items-start justify-between gap-2">
          <span className="flex-1">{flash}</span>
          <button data-testid="admin-users-flash-dismiss"
            onClick={() => setFlash('')}
            className="text-emerald-700 hover:text-emerald-900 font-bold">×</button>
        </div>
      )}

      {/* Iter-52.43 — Adaptive summary strip. Renders whichever filter
          combo is currently active so the admin sees a one-line
          headline instead of counting rows. Multiple filters stack
          into a comma-separated line. Updates live via `total` on
          every backend response. */}
      {(() => {
        const parts = [];
        if (regPeriod !== 'off') {
          const label = regPeriod === '7d' ? 'last 7 days'
                      : regPeriod === '15d' ? 'last 15 days'
                      : regPeriod === 'today' ? 'today'
                      : regPeriod === 'yesterday' ? 'yesterday'
                      : regPeriod === '2d_before' ? '2 days ago'
                      : regPeriod === '3d_before' ? '3 days ago'
                      : regPeriod === 'custom' && regFrom && regTo ? `${regFrom} → ${regTo}`
                      : 'selected period';
          parts.push(`${total} registered ${label}`);
        }
        if (childPeriod !== 'off') {
          const label = childPeriod === '7d' ? 'in last 7 days'
                      : childPeriod === '15d' ? 'in last 15 days'
                      : childPeriod === 'today' ? 'today'
                      : childPeriod === 'yesterday' ? 'yesterday'
                      : childPeriod === '2d_before' ? '2 days ago'
                      : childPeriod === '3d_before' ? '3 days ago'
                      : childPeriod === 'custom' && childFrom && childTo ? `between ${childFrom} → ${childTo}`
                      : 'in selected period';
          parts.push(`${total} added a new kid profile ${label}`);
        }
        if (zeroPeriod !== 'off') {
          const winStr = zeroWindow
            ? (zeroWindow.from === zeroWindow.to ? zeroWindow.from : `${zeroWindow.from} → ${zeroWindow.to}`)
            : 'this period';
          parts.push(`${total} ${includeLow ? 'low-activity (0-2)' : 'zero-activity'} users in ${winStr}`);
        }
        if (onlyFlagged) parts.push(`${total} flagged for manual review`);
        // Iter-52.46 — reflect trial-status filter in the summary.
        if (trialStatus === 'trial') parts.push(`${total} currently on trial`);
        else if (trialStatus === 'active') parts.push(`${total} paid / active`);
        else if (trialStatus === 'expired') parts.push(`${total} expired`);
        // Iter-52.47 — extra-profile-request filter reflected in summary.
        if (extraPeriod !== 'off') {
          const label = extraPeriod === '7d' ? 'in last 7 days'
                      : extraPeriod === '15d' ? 'in last 15 days'
                      : extraPeriod === 'today' ? 'today'
                      : extraPeriod === 'yesterday' ? 'yesterday'
                      : extraPeriod === '2d_before' ? '2 days ago'
                      : extraPeriod === '3d_before' ? '3 days ago'
                      : extraPeriod === 'custom' && extraFrom && extraTo ? `between ${extraFrom} → ${extraTo}`
                      : 'in selected period';
          parts.push(`${total} requested extra profile ${label}`);
        }
        // Iter-52.48 — gift-unused filter reflected in summary.
        if (giftUnusedOnly) {
          parts.push(`${total} gifted profile${total === 1 ? '' : 's'} not yet used`);
        }
        if (parts.length === 0 && q) parts.push(`${total} match "${q}"`);
        if (parts.length === 0) parts.push(`${total} users`);
        const headline = parts.join(' · ');
        return (
          <div
            data-testid="admin-users-summary-strip"
            className="mb-2 px-3 py-2 rounded-2xl bg-indigo-50 border border-indigo-200 text-[13px] font-bold text-indigo-900 flex items-center gap-2 flex-wrap"
          >
            <span className="text-lg leading-none">📊</span>
            <span data-testid="admin-users-summary-text">{headline}</span>
            {busy && <span className="text-[11px] font-normal text-indigo-500 italic">refreshing…</span>}
          </div>
        );
      })()}

      <div className="relative">
        <div
          data-testid="admin-users-scroll"
          className="admin-users-fade border border-slate-200 rounded-lg overflow-x-auto overflow-y-auto max-h-96 [-webkit-overflow-scrolling:touch]"
        >
        <table className="w-full min-w-[1500px] text-left text-[13px]">
          <thead className="bg-slate-100 text-slate-700 sticky top-0 z-10">
            <tr>
              <th className="px-2 py-2 w-6"></th>
              <th className="px-2 py-2 w-14 sticky left-6 bg-slate-100 z-20 shadow-[2px_0_0_0_rgb(226_232_240)] text-center">#</th>
              <th className="px-2 py-2 sticky left-20 bg-slate-100 z-20 shadow-[2px_0_0_0_rgb(226_232_240)]">email</th>
              <th className="px-2 py-2">mobile</th>
              <th className="px-2 py-2">status</th>
              <th className="px-2 py-2">plan</th>
              <th className="px-2 py-2">registered</th>
              <th className="px-2 py-2">location</th>
              <th className="px-2 py-2">ad source</th>
              <th className="px-2 py-2">expires</th>
              <th className="px-2 py-2">kids</th>
              <th className="px-2 py-2">last active</th>
              <th className="px-2 py-2">usage (30d)</th>
              {zeroPeriod !== 'off' && (
                <th className="px-2 py-2" data-testid="admin-users-activity-in-period-th">
                  activity in period
                </th>
              )}
              <th className="px-2 py-2">pages</th>
              <th className="px-2 py-2">est. cost</th>
              <th className="px-2 py-2">actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const isOpen = !!expanded[r.user_id];
              const flagged = !!r.over_profile_needs_manual_review;
              return (
                <Fragment key={r.user_id}>
                <tr data-testid={`admin-user-${r.user_id}`}
                  className={`border-t border-slate-200 align-top ${flagged ? 'bg-amber-50' : ''}`}>
                  <td className="px-1 py-2 sticky left-0 bg-white z-10">
                    <button data-testid={`admin-user-expand-${r.user_id}`}
                      onClick={() => setExpanded(x => ({ ...x, [r.user_id]: !x[r.user_id] }))}
                      className="p-0.5 rounded hover:bg-slate-100">
                      {isOpen ? <ChevronDown size={16}/> : <ChevronRight size={16}/>}
                    </button>
                  </td>
                  {/* Iter-51.67 — stable parent rank column (sticky). #1
                      = oldest-ever signup, never changes. Combined with
                      creation_rank in the expand row it gives every
                      profile on the platform a unique stable ID like
                      "5a" = oldest child of the 5th-ever parent. */}
                  <td className={`px-1 py-2 sticky left-6 z-10 shadow-[2px_0_0_0_rgb(226_232_240)] text-center ${flagged ? 'bg-amber-50' : 'bg-white'}`}>
                    <span data-testid={`admin-user-rank-${r.user_id}`}
                      className="inline-flex items-center justify-center min-w-[28px] h-7 px-1.5 rounded-full bg-slate-800 text-white font-bold text-[12px]"
                      title={`Family #${r.parent_rank || '?'} by stable creation order`}>
                      #{r.parent_rank || '?'}
                    </span>
                  </td>
                  <td className={`px-2 py-2 whitespace-nowrap sticky left-20 z-10 shadow-[2px_0_0_0_rgb(226_232_240)] ${flagged ? 'bg-amber-50' : 'bg-white'}`}>
                    <div className="font-mono text-slate-800">{r.email || '—'}</div>
                    <div className="text-slate-500 truncate max-w-[16ch]">{r.name || ''}</div>
                    {/* Iter-51.65 — every account gets an explicit chip. */}
                    {(() => {
                      const st = r.watchdog_status || 'clear';
                      const chip = WATCHDOG_CHIP[st] || WATCHDOG_CHIP.clear;
                      return (
                        <div className="mt-0.5 flex items-center gap-1 flex-wrap">
                          <span data-testid={`admin-user-watchdog-${r.user_id}`}
                            className={`inline-block px-1.5 py-0 rounded-full text-[10px] font-bold uppercase tracking-wider ${chip.cls}`}>
                            {chip.label}
                          </span>
                          {r.is_test_account && st !== 'exempt' && (
                            <span className="text-[10px] text-slate-500 inline-flex items-center gap-0.5">
                              <ShieldCheck size={10}/> test
                            </span>
                          )}
                        </div>
                      );
                    })()}
                  </td>
                  <td className="px-2 py-2 whitespace-nowrap text-slate-700 font-mono">
                    {r.mobile_e164 ? (
                      <a href={`https://wa.me/${r.mobile_e164.replace(/\D/g,'')}`} target="_blank" rel="noopener noreferrer"
                        title="Open WhatsApp"
                        className="text-emerald-700 hover:underline"
                        data-testid={`admin-user-mobile-${r.user_id}`}>
                        {r.mobile_e164}
                      </a>
                    ) : <span className="text-slate-400">—</span>}
                  </td>
                  <td className="px-2 py-2 whitespace-nowrap" data-testid={`admin-user-status-cell-${r.user_id}`}>
                    <span className={`inline-block px-2 py-0.5 rounded-full text-[11px] font-bold uppercase ${chipCls(r.status)}`}>{r.status}</span>
                    {/* Iter-52.46 — days-left tag INLINE with the
                        status chip so the countdown is unmissable
                        (no need to scan across to the expiry column).
                        Colour bands match the pill under the expiry
                        date so both surfaces read consistently. */}
                    {r.status === 'trial' && Number.isFinite(r.days_remaining) && (
                      <span
                        data-testid={`admin-user-status-days-${r.user_id}`}
                        className={`block mt-1 text-[11px] font-extrabold ${
                          r.days_remaining <= 2 ? 'text-rose-700'
                          : r.days_remaining <= 7 ? 'text-amber-700'
                          : 'text-emerald-700'
                        }`}
                      >
                        {r.days_remaining === 0 ? 'expires today' : `${r.days_remaining} ${r.days_remaining === 1 ? 'day' : 'days'} left`}
                      </span>
                    )}
                    {r.status === 'expired' && (
                      <span
                        data-testid={`admin-user-status-days-${r.user_id}`}
                        className="block mt-1 text-[11px] font-extrabold text-slate-500"
                      >
                        trial ended
                      </span>
                    )}
                  </td>
                  {/* Iter-51.94 — Plan tier + billing cycle chip so
                      admins can see WHICH plan each user is on, not
                      just Active/Trial/Expired. */}
                  <td className="px-2 py-2 whitespace-nowrap" data-testid={`admin-user-plan-${r.user_id}`}>
                    {(() => {
                      const p = planChipParts(r);
                      const cls = PLAN_TIER_CHIP[p.tier] || PLAN_TIER_CHIP.starter;
                      return (
                        <div className="flex flex-col gap-0.5">
                          <span className={`inline-block px-2 py-0.5 rounded-full text-[11px] font-bold uppercase tracking-wide ${cls}`}>
                            {p.label}
                          </span>
                          {p.cycle && (
                            <span className="text-slate-500 text-[11px]">{p.cycle}</span>
                          )}
                        </div>
                      );
                    })()}
                  </td>
                  {/* Iter-52.28 — Registered on: exact timestamp
                      (e.g. "22 Sep, 2:15 PM") as the primary label so
                      admins can cross-reference against Facebook Ads
                      Manager, with the relative "3h ago" tucked below
                      for at-a-glance recency. */}
                  <td className="px-2 py-2 whitespace-nowrap text-slate-700" data-testid={`admin-user-registered-${r.user_id}`}>
                    {r.created_at ? (
                      <>
                        <div className="font-medium">{exactTime(r.created_at)}</div>
                        <div className="text-slate-500 text-[11px]">{relTime(r.created_at)}</div>
                      </>
                    ) : <span className="text-slate-400">—</span>}
                  </td>
                  {/* Iter-52.25 — Location chip (City, State abbrev). */}
                  <td className="px-2 py-2 whitespace-nowrap" data-testid={`admin-user-location-${r.user_id}`}>
                    {(r.city || r.state) ? (
                      <span className="inline-block px-2 py-0.5 rounded-full text-[11px] font-bold bg-teal-100 text-teal-800">
                        {[r.city, r.state].filter(Boolean).join(', ')}
                      </span>
                    ) : <span className="text-slate-400">—</span>}
                  </td>
                  {/* Iter-52.25 — Ad source (first-touch UTM). Compact
                      chip; tap to expand a details popover showing the
                      full attribution dict. */}
                  <td className="px-2 py-2 whitespace-nowrap" data-testid={`admin-user-adsource-${r.user_id}`}>
                    <AdSourceCell attribution={r.attribution} userId={r.user_id} />
                  </td>
                  <td className="px-2 py-2 whitespace-nowrap text-slate-700">
                    {r.access_expires_at ? new Date(r.access_expires_at).toLocaleDateString() : '—'}
                    {/* Iter-52.38 — trial-urgency pill. Only rendered
                        while the account is on trial so paid rows
                        don't get a misleading countdown. Colour bands:
                          • 0-2  days → rose  (URGENT — outreach today)
                          • 3-7  days → amber (nudge this week)
                          • 8+   days → emerald (comfortable)
                          • <0        → slate strikethrough (expired) */}
                    {(() => {
                      // Iter-52.38 — Show pill for trial + expired
                      // trial accounts (paid subscriptions skip it).
                      // `days_remaining` is server-clamped to 0, so
                      // we lean on `status === 'expired'` to decide
                      // the slate "expired" state rather than checking
                      // for negatives.
                      const s = r.status;
                      if (s !== 'trial' && s !== 'expired') return null;
                      const d = Number.isFinite(r.days_remaining) ? r.days_remaining : null;
                      if (d === null && s !== 'expired') return null;
                      let cls, text;
                      if (s === 'expired') {
                        cls = 'bg-slate-200 text-slate-600 line-through';
                        text = 'expired';
                      } else if (d <= 2) {
                        cls = 'bg-rose-100 text-rose-800';
                        text = `${d}d left`;
                      } else if (d <= 7) {
                        cls = 'bg-amber-100 text-amber-800';
                        text = `${d}d left`;
                      } else {
                        cls = 'bg-emerald-100 text-emerald-800';
                        text = `${d}d left`;
                      }
                      return (
                        <div className="mt-0.5">
                          <span
                            data-testid={`admin-user-trial-pill-${r.user_id}`}
                            className={`inline-block px-2 py-0.5 rounded-full text-[11px] font-bold ${cls}`}
                            title={s === 'expired' ? 'Trial has expired' : `Trial days remaining: ${d}`}
                          >
                            {text}
                          </span>
                        </div>
                      );
                    })()}
                  </td>
                  <td className="px-2 py-2 whitespace-nowrap" data-testid={`admin-user-kids-${r.user_id}`}>
                    {/* Iter-52.48 — distinct multi-child badge so
                        admins can spot families with 2+ profiles at
                        a glance (higher LTV, worth prioritising for
                        outreach & upsell). Single-profile accounts
                        keep the quiet numeric render so the visual
                        weight in the table only accrues where it's
                        meaningful. */}
                    <div className="flex flex-col gap-1">
                      {(() => {
                        const n = Number(r.profile_count) || 0;
                        if (n <= 1) {
                          return <span className="text-slate-700">{n}</span>;
                        }
                        return (
                          <span
                            data-testid={`admin-user-multi-kid-chip-${r.user_id}`}
                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold bg-fuchsia-100 text-fuchsia-800 border border-fuchsia-200 self-start"
                            title={`${n} student profiles on this account`}
                          >
                            👨‍👩‍👧 {n} profiles
                          </span>
                        );
                      })()}
                      {/* Iter-52.47 — Extra-profile-request chip.
                          Rendered on any row where the parent has
                          tapped the WhatsApp handoff at least once
                          (fields sourced from admin_list_users' new
                          extra_profile_request_count field). Tooltip
                          shows exact last-requested timestamp so the
                          admin can prioritise fresh requests. */}
                      {(Number(r.extra_profile_request_count) || 0) > 0 && (
                        <span
                          data-testid={`admin-user-extra-profile-chip-${r.user_id}`}
                          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold bg-violet-100 text-violet-800 border border-violet-200 self-start"
                          title={
                            r.extra_profile_last_requested_at
                              ? `Last tapped ${new Date(r.extra_profile_last_requested_at).toLocaleString()}`
                              : 'Tapped the "I want an extra profile" WhatsApp handoff'
                          }
                        >
                          💬 Wants extra profile{r.extra_profile_request_count > 1 ? ` · ${r.extra_profile_request_count}×` : ''}
                        </span>
                      )}
                      {/* Iter-52.48 — Gifted-but-not-used chip. Only
                          shows when this parent was granted an extra
                          slot (`extra_profile_bonus > 0`) but hasn't
                          added a second kid yet. Tooltip shows when
                          the gift was granted so admin can prioritise
                          the ones that have been sitting longest. */}
                      {r.gift_unused && (
                        <span
                          data-testid={`admin-user-gift-unused-chip-${r.user_id}`}
                          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold bg-amber-100 text-amber-800 border border-amber-200 self-start"
                          title={
                            r.latest_bonus_granted_at
                              ? `Gifted on ${new Date(r.latest_bonus_granted_at).toLocaleString()} — parent hasn't added a 2nd child yet`
                              : "Gifted an extra profile slot — parent hasn't added a 2nd child yet"
                          }
                        >
                          🎁 Gifted, not used
                          {r.latest_bonus_granted_at ? ` · ${relTime(r.latest_bonus_granted_at)}` : ''}
                        </span>
                      )}
                      {/* Iter-52.50 — Answer-Help usage chip. Total
                          weekly count aggregated over ALL of this
                          parent's kids, with a per-type breakdown
                          on tooltip so admin sees which formats the
                          child leaned on most (long vs mcq vs match). */}
                      {(Number(r.answer_help_this_week) || 0) > 0 && (
                        <span
                          data-testid={`admin-user-answer-help-chip-${r.user_id}`}
                          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold bg-emerald-100 text-emerald-800 border border-emerald-200 self-start"
                          title={(() => {
                            const bt = r.answer_help_by_type || {};
                            const parts = Object.entries(bt).map(([k, v]) => {
                              const label = k === 'long_answer' ? 'Long'
                                          : k === 'fill_blank' ? 'Fill'
                                          : k === 'mcq' ? 'MCQ'
                                          : k === 'match_the_following' ? 'Match'
                                          : k;
                              return `${label}: ${v}`;
                            });
                            return parts.length ? `Last 7 days — ${parts.join(' · ')}` : 'Answer-Help attempts last 7 days';
                          })()}
                        >
                          ✏️ Answer Help {r.answer_help_this_week}×
                        </span>
                      )}
                      {/* Iter-52.55.2 — 7-day rescan count so admins spot
                          users who repeatedly tap Rescan (cost signal). */}
                      {(Number(r.rescans_this_week) || 0) > 0 && (
                        <span
                          data-testid={`admin-user-rescans-chip-${r.user_id}`}
                          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold bg-blue-100 text-blue-800 border border-blue-200 self-start"
                          title="Rescans triggered in last 7 days (3/day limit)"
                        >
                          🔄 Rescan {r.rescans_this_week}×
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-2 py-2 whitespace-nowrap text-slate-700" data-testid={`admin-user-lastactive-${r.user_id}`}>
                    {r.last_active_at ? (
                      <>
                        <div>{new Date(r.last_active_at).toLocaleDateString()}</div>
                        <div className="text-slate-500 text-[11px]">{relTime(r.last_active_at)}</div>
                      </>
                    ) : <span className="text-slate-400">—</span>}
                  </td>
                  <td className="px-2 py-2 whitespace-nowrap" data-testid={`admin-user-usage-${r.user_id}`}>
                    <span className={`inline-block px-2 py-0.5 rounded-full text-[11px] font-bold ${usageCls(r.usage_label)}`}>
                      {r.usage_label || 'Inactive'}
                    </span>
                    <div className="text-slate-500 text-[11px] mt-0.5">
                      {r.sessions_30d ?? 0} in 30d · {r.sessions_7d ?? 0} in 7d
                    </div>
                  </td>
                  {zeroPeriod !== 'off' && (
                    <td
                      className="px-2 py-2 whitespace-nowrap"
                      data-testid={`admin-user-activity-in-period-${r.user_id}`}
                    >
                      <span
                        className={`inline-block px-2 py-0.5 rounded-full text-[11px] font-bold ${
                          (r.activity_in_period ?? 0) === 0
                            ? 'bg-rose-100 text-rose-800'
                            : 'bg-amber-100 text-amber-800'
                        }`}
                      >
                        {r.activity_in_period ?? 0}
                      </span>
                    </td>
                  )}
                  <td className="px-2 py-2 whitespace-nowrap text-slate-700 font-mono" data-testid={`admin-user-pages-${r.user_id}`}>
                    {r.total_pages ?? 0}
                  </td>
                  <td className="px-2 py-2 whitespace-nowrap font-mono text-amber-700" data-testid={`admin-user-cost-${r.user_id}`}>
                    {formatInr(r.est_cost_inr)}
                  </td>
                  <td className="px-2 py-2 whitespace-nowrap">
                    <div className="inline-flex gap-1 flex-wrap">
                      {flagged && (
                        <button data-testid={`admin-user-clear-flag-${r.user_id}`} onClick={() => clearFlag(r)}
                          className="text-[11px] font-bold px-2 py-1 rounded-full bg-amber-600 text-white hover:bg-amber-700 inline-flex items-center gap-1">
                          <ShieldCheck size={11}/> Clear flag
                        </button>
                      )}
                      <button data-testid={`admin-user-activate-${r.user_id}`} onClick={() => openActivate(r)}
                        className="text-[11px] font-bold px-2 py-1 rounded-full bg-emerald-600 text-white hover:bg-emerald-700 inline-flex items-center gap-1">
                        <Zap size={11}/> Activate
                      </button>
                      <button data-testid={`admin-user-extend-${r.user_id}`} onClick={() => openExtend(r)}
                        className="text-[11px] font-bold px-2 py-1 rounded-full bg-amber-500 text-white hover:bg-amber-600 inline-flex items-center gap-1">
                        <CalendarClock size={11}/> Extend
                      </button>
                      {/* Iter-52.20 — gift/revoke a free extra profile.
                          Sits next to Extend since it's the same
                          "grant them something for free" flow. Label
                          reflects the CURRENT bonus so admin sees at
                          a glance whether they've already gifted. */}
                      <button data-testid={`admin-user-profile-bonus-${r.user_id}`} onClick={() => openBonus(r)}
                        className="text-[11px] font-bold px-2 py-1 rounded-full bg-indigo-600 text-white hover:bg-indigo-700 inline-flex items-center gap-1">
                        <Gift size={11}/> {r.extra_profile_bonus > 0 ? `Profiles +${r.extra_profile_bonus}` : 'Gift profile'}
                      </button>
                      {/* Iter-52.48 — Retroactively log an off-app
                          WhatsApp / DM request for an extra profile.
                          Admins message parents directly all the time
                          (screenshots, business cards, Instagram DMs)
                          and those requests never fire the in-app
                          tracker. This button records one against
                          this parent so the "Wants extra profile"
                          chip + counters reflect the real ask. */}
                      <button
                        data-testid={`admin-user-log-wa-request-${r.user_id}`}
                        onClick={async () => {
                          const who = r.name || r.email || r.mobile_e164 || r.user_id;
                          if (!window.confirm(`Log a WhatsApp/DM request from ${who}?\n\nUse this when a parent asked you directly (not via the in-app button) — the "Wants extra profile" chip will update immediately.`)) return;
                          setErr(''); setFlash('');
                          try {
                            const { data } = await api.post('/admin/users/log-extra-profile-request', {
                              pin: debugPin,
                              user_id: r.user_id,
                              email: r.email || undefined,
                              mobile_e164: r.mobile_e164 || undefined,
                              source: 'admin_manual_whatsapp',
                            });
                            setFlash(`Logged WA request for ${who}. Total requests: ${data.extra_profile_request_count}×`);
                            await load();
                          } catch (e) {
                            setErr(coerceError(e, 'Failed to log request.'));
                          }
                        }}
                        className="text-[11px] font-bold px-2 py-1 rounded-full bg-violet-600 text-white hover:bg-violet-700 inline-flex items-center gap-1"
                        title="Log an off-app WhatsApp/DM request so it counts in the tracker"
                      >
                        <MessageSquare size={11}/> Log WA req
                      </button>
                      <button data-testid={`admin-user-delete-${r.user_id}`} onClick={() => openDelete(r)}
                        className="text-[11px] font-bold px-2 py-1 rounded-full bg-rose-600 text-white hover:bg-rose-700 inline-flex items-center gap-1">
                        <Trash2 size={11}/> Delete
                      </button>
                    </div>
                  </td>
                </tr>
                {isOpen && (
                  <tr key={`${r.user_id}-children`} data-testid={`admin-user-children-${r.user_id}`}
                    className="bg-slate-50 border-t border-slate-200">
                    <td colSpan={zeroPeriod !== 'off' ? 17 : 16} className="px-4 py-2">
                      <div className="text-[12px] font-bold text-slate-700 mb-1 uppercase tracking-wider flex items-center gap-2">
                        Children profiles ({(r.children || []).length})
                        {(r.children || []).length > 0 && (
                          <span className="text-slate-500 font-normal normal-case tracking-normal text-[11px]">
                            (sorted by most recent activity — plan changes count too)
                          </span>
                        )}
                      </div>
                      {(r.children || []).length === 0 ? (
                        <div className="text-[12px] text-slate-500 italic">No profiles yet.</div>
                      ) : (
                        <table className="w-full text-[12px] border border-slate-200 rounded bg-white">
                          <thead className="bg-slate-100 text-slate-600">
                            <tr>
                              <th className="px-2 py-1 text-center w-14">#</th>
                              <th className="px-2 py-1 text-left">name</th>
                              <th className="px-2 py-1 text-left">created</th>
                              <th className="px-2 py-1 text-left">last activity</th>
                              <th className="px-2 py-1 text-left">child_id</th>
                              <th className="px-2 py-1 text-right">pages</th>
                              <th className="px-2 py-1 text-right">points</th>
                              <th className="px-2 py-1 text-right">streak</th>
                              <th className="px-2 py-1 text-center">PIN</th>
                            </tr>
                          </thead>
                          <tbody>
                            {/* Iter-51.65 — sort by `last_activity_at`
                                DESC (recently-active families rise to
                                the top). `creation_rank` from backend
                                stays fixed at 1..N by original created_at
                                so admins can always track "which sibling
                                was first" even when display order shifts. */}
                            {[...(r.children || [])]
                              .sort((a, b) => (b.last_activity_at || b.created_at || '').localeCompare(a.last_activity_at || a.created_at || ''))
                              .map((c) => {
                                // Explain WHY this child sits where it does — tap-target for admin clarity.
                                const bump =
                                  c.last_activity_at && r.plan_last_updated_at && c.last_activity_at === r.plan_last_updated_at
                                    ? 'plan updated'
                                    : c.last_activity_at && c.last_session_date && c.last_activity_at === c.last_session_date
                                    ? 'read session'
                                    : 'created';
                                return (
                              <tr key={c.child_id} data-testid={`admin-child-${c.child_id}`} className="border-t border-slate-100">
                                <td className="px-2 py-1 text-center">
                                  {/* Iter-51.67 — compound stable ID:
                                      "5a" = oldest child of the 5th-ever
                                      parent. `creation_rank` from the
                                      backend is 1-indexed and stable;
                                      we map 1→a, 2→b, 3→c… */}
                                  <span className="inline-flex items-center justify-center min-w-[36px] h-6 px-2 rounded-full bg-slate-800 text-white font-bold text-[11px] font-mono"
                                    title={`Profile #${r.parent_rank}${String.fromCharCode(96 + c.creation_rank)} — sibling ${c.creation_rank} of family ${r.parent_rank}`}
                                    data-testid={`admin-child-seq-${c.child_id}`}>
                                    #{r.parent_rank}{String.fromCharCode(96 + c.creation_rank)}
                                  </span>
                                </td>
                                <td className="px-2 py-1 text-slate-800 font-bold">{c.name || '—'}</td>
                                <td className="px-2 py-1 text-slate-600">
                                  {c.created_at ? (
                                    <>
                                      <div>{fmtDateShort(c.created_at)}</div>
                                      <div className="text-slate-400 text-[11px]">{relTime(c.created_at)}</div>
                                    </>
                                  ) : '—'}
                                </td>
                                <td className="px-2 py-1 text-slate-700" data-testid={`admin-child-lastactivity-${c.child_id}`}>
                                  {c.last_activity_at ? (
                                    <>
                                      <div>{fmtDateShort(c.last_activity_at)}</div>
                                      <div className="text-slate-400 text-[11px]">{bump}</div>
                                    </>
                                  ) : <span className="text-slate-400">—</span>}
                                </td>
                                <td className="px-2 py-1 font-mono text-slate-500">{c.child_id.slice(-8)}</td>
                                <td className={`px-2 py-1 text-right font-mono ${c.pages > 0 ? 'text-emerald-700 font-bold' : 'text-slate-400'}`}>
                                  {c.pages}
                                </td>
                                <td className="px-2 py-1 text-right font-mono text-slate-700">{c.total_points}</td>
                                <td className="px-2 py-1 text-right font-mono text-slate-700">{c.streak}</td>
                                <td className="px-2 py-1 text-center">
                                  {c.has_pin ? <ShieldCheck size={12} className="inline text-emerald-700"/> : <span className="text-slate-400">—</span>}
                                </td>
                              </tr>
                            );
                          })}
                          </tbody>
                        </table>
                      )}
                      {r.over_profile_scheduled_delete_at && (
                        <div className="mt-2 text-[12px] text-rose-700 bg-rose-50 border border-rose-200 rounded px-2 py-1">
                          Extra profiles scheduled for deletion: {new Date(r.over_profile_scheduled_delete_at).toLocaleDateString()}
                        </div>
                      )}
                    </td>
                  </tr>
                )}
                </Fragment>
              );
            })}
            {!rows.length && !busy && (
              <tr><td colSpan={zeroPeriod !== 'off' ? 17 : 16} className="px-2 py-4 text-center text-slate-500 italic">
                {onlyFlagged ? 'No accounts currently flagged for manual review.' : 'No users match.'}
              </td></tr>
            )}
          </tbody>
        </table>
        </div>
      </div>
      <div className="sm:hidden text-[11px] text-slate-500 mt-1 italic" data-testid="admin-users-swipe-hint">
        Swipe the table sideways to see status, actions, and usage columns →
      </div>

      {editing && (
        <div data-testid="admin-extend-modal" className="mt-3 border border-slate-300 rounded-lg p-3 bg-slate-50">
          <div className="text-[13px] font-bold text-amber-700 mb-2 flex items-center gap-2">
            <ClipboardPaste size={13}/> Extend access — {editing.email}
          </div>
          <div className="flex flex-col gap-2">
            <label className="text-[12px] text-slate-600">
              New expiry date
              <input data-testid="admin-extend-date" type="date" value={newDate}
                onChange={(e) => setNewDate(e.target.value)}
                className="w-full mt-1 bg-white text-slate-800 text-[13px] rounded px-2 py-1.5 outline-none border border-slate-300"/>
            </label>
            <label className="text-[12px] text-slate-600">
              Note (optional — e.g. UPI txn id)
              <input data-testid="admin-extend-note" value={note} onChange={(e) => setNote(e.target.value)}
                placeholder="Paid via UPI on ..."
                className="w-full mt-1 bg-white text-slate-800 text-[13px] rounded px-2 py-1.5 outline-none border border-slate-300"/>
            </label>
            {/* Iter-51.72 — inline error/success surface. Prior version
                stored feedback in the shared `err` / `flash` state, which
                rendered up in the users card header — hidden behind the
                open modal. Admins clicking Save/Activate saw nothing and
                assumed the action was silently rejected. */}
            {err && (
              <div data-testid="admin-extend-error"
                className="text-[13px] text-rose-700 bg-rose-50 border border-rose-300 rounded px-2 py-1.5 font-bold">
                {err}
              </div>
            )}
            {flash && (
              <div data-testid="admin-extend-flash"
                className="text-[13px] text-emerald-800 bg-emerald-50 border border-emerald-300 rounded px-2 py-1.5 font-bold">
                ✓ {flash}
              </div>
            )}
            <div className="flex gap-2 pt-1">
              <button data-testid="admin-extend-save" onClick={save} disabled={saving || !newDate}
                className="text-[12px] font-bold px-3 py-1.5 rounded-full bg-emerald-600 text-white disabled:opacity-60">
                {saving ? 'Saving…' : 'Save'}
              </button>
              <button data-testid="admin-extend-cancel" onClick={() => { setEditing(null); setErr(''); setFlash(''); }}
                className="text-[12px] font-bold px-3 py-1.5 rounded-full bg-slate-200 text-slate-800">Cancel</button>
            </div>
          </div>
        </div>
      )}
      {/* Iter-52.20 — profile bonus modal */}
      {bonusFor && (
        <div data-testid="admin-profile-bonus-modal" className="mt-3 border border-indigo-300 rounded-lg p-3 bg-indigo-50">
          <div className="text-[13px] font-bold text-indigo-800 mb-2 flex items-center gap-2">
            <Gift size={13}/> Gift extra profile — {bonusFor.email || bonusFor.mobile_e164 || bonusFor.user_id}
          </div>
          <div className="text-[12px] text-slate-700 mb-2">
            Current bonus: <b>+{bonusFor.extra_profile_bonus || 0}</b> · Effective limit: <b>{bonusFor.effective_profile_limit || 1}</b> profiles.
          </div>
          <div className="flex flex-col gap-2">
            <label className="text-[12px] text-slate-600">
              Change (use +1 to gift a slot, -1 to revoke)
              <input data-testid="admin-profile-bonus-delta" type="number" step={1} value={bonusDelta}
                onChange={(e) => setBonusDelta(e.target.value)}
                className="w-full mt-1 bg-white text-slate-800 text-[13px] rounded px-2 py-1.5 outline-none border border-slate-300"/>
            </label>
            <label className="text-[12px] text-slate-600">
              Note (optional — e.g. "Facebook trial user, gifted 2nd profile 20-Feb")
              <input data-testid="admin-profile-bonus-note" value={bonusNote} onChange={(e) => setBonusNote(e.target.value)}
                placeholder="Reason for this grant…"
                className="w-full mt-1 bg-white text-slate-800 text-[13px] rounded px-2 py-1.5 outline-none border border-slate-300"/>
            </label>
            {err && (
              <div data-testid="admin-profile-bonus-error"
                className="text-[13px] text-rose-700 bg-rose-50 border border-rose-300 rounded px-2 py-1.5 font-bold">
                {err}
              </div>
            )}
            {flash && (
              <div data-testid="admin-profile-bonus-flash"
                className="text-[13px] text-emerald-800 bg-emerald-50 border border-emerald-300 rounded px-2 py-1.5 font-bold">
                ✓ {flash}
              </div>
            )}
            <div className="flex gap-2 pt-1">
              <button data-testid="admin-profile-bonus-save" onClick={saveBonus} disabled={bonusSaving || !bonusDelta}
                className="text-[12px] font-bold px-3 py-1.5 rounded-full bg-indigo-600 text-white disabled:opacity-60">
                {bonusSaving ? 'Saving…' : 'Apply'}
              </button>
              <button data-testid="admin-profile-bonus-cancel" onClick={() => { setBonusFor(null); setErr(''); setFlash(''); }}
                className="text-[12px] font-bold px-3 py-1.5 rounded-full bg-slate-200 text-slate-800">Cancel</button>
            </div>
          </div>
        </div>
      )}
      {activating && (
        <div data-testid="admin-activate-modal" className="mt-3 border border-emerald-300 rounded-lg p-3 bg-emerald-50">
          <div className="text-[13px] font-bold text-emerald-800 mb-2 flex items-center gap-2">
            <Zap size={13}/> Activate plan — {activating.email}
          </div>
          <div className="flex flex-col gap-2">
            <label className="text-[12px] text-slate-600">
              Billing cycle
              <select data-testid="admin-activate-plan" value={actPlan}
                onChange={(e) => setActPlan(e.target.value)}
                className="w-full mt-1 bg-white text-slate-800 text-[13px] rounded px-2 py-1.5 outline-none border border-slate-300">
                <option value="monthly">Monthly — +1 month of access</option>
                <option value="annual">Annual — +13 months (12 paid + 1 bonus)</option>
              </select>
            </label>
            <label className="text-[12px] text-slate-600">
              Plan tier (drives page limit)
              <select data-testid="admin-activate-tier" value={actTier}
                onChange={(e) => setActTier(e.target.value)}
                className="w-full mt-1 bg-white text-slate-800 text-[13px] rounded px-2 py-1.5 outline-none border border-slate-300">
                <option value="starter">Starter — 20 active pages</option>
                <option value="growth">Growth — 35 active pages</option>
                <option value="standard">Standard — 60 active pages</option>
                <option value="premium">Premium — 100 active pages</option>
              </select>
            </label>
            <label className="text-[12px] text-slate-600">
              Starts on (defaults to today)
              <input data-testid="admin-activate-start" type="date" value={actStart}
                onChange={(e) => setActStart(e.target.value)}
                className="w-full mt-1 bg-white text-slate-800 text-[13px] rounded px-2 py-1.5 outline-none border border-slate-300"/>
            </label>
            <label className="text-[12px] text-slate-600">
              Note (optional — e.g. UPI txn id)
              <input data-testid="admin-activate-note" value={actNote} onChange={(e) => setActNote(e.target.value)}
                placeholder="Paid via UPI on ..."
                className="w-full mt-1 bg-white text-slate-800 text-[13px] rounded px-2 py-1.5 outline-none border border-slate-300"/>
            </label>
            {/* Iter-51.72 — inline feedback so admins see the outcome
                without scrolling behind the open modal. */}
            {err && (
              <div data-testid="admin-activate-error"
                className="text-[13px] text-rose-700 bg-rose-50 border border-rose-300 rounded px-2 py-1.5 font-bold">
                {err}
              </div>
            )}
            {flash && (
              <div data-testid="admin-activate-flash"
                className="text-[13px] text-emerald-800 bg-white border border-emerald-400 rounded px-2 py-1.5 font-bold">
                ✓ {flash}
              </div>
            )}
            <div className="flex gap-2 pt-1">
              <button data-testid="admin-activate-save" onClick={saveActivate} disabled={actSaving}
                className="text-[12px] font-bold px-3 py-1.5 rounded-full bg-emerald-600 text-white disabled:opacity-60">
                {actSaving ? 'Activating…' : 'Activate'}
              </button>
              <button data-testid="admin-activate-cancel" onClick={() => { setActivating(null); setErr(''); setFlash(''); }}
                className="text-[12px] font-bold px-3 py-1.5 rounded-full bg-slate-200 text-slate-800">Cancel</button>
            </div>
          </div>
        </div>
      )}
      {deleting && (
        <div data-testid="admin-delete-modal" className="mt-3 border border-rose-300 rounded-lg p-3 bg-rose-50">
          <div className="text-[13px] font-bold text-rose-800 mb-2 flex items-center gap-2">
            <Trash2 size={13}/> Delete account — {deleting.email || deleting.mobile_e164 || deleting.user_id}
          </div>
          <div className="text-[13px] text-rose-900 bg-white border border-rose-200 rounded p-2 mb-2 leading-relaxed">
            <div className="font-bold mb-1">This cannot be undone.</div>
            The following will be permanently removed:
            <ul className="list-disc pl-4 mt-1 space-y-0.5">
              <li>Parent account, profile, and login sessions</li>
              <li>{deleting.profile_count ?? 0} student profile{deleting.profile_count === 1 ? '' : 's'}</li>
              <li>All uploaded books, reading sessions, and quiz runs for those profiles</li>
              <li>Rewards, moderation flags, and PIN unlock history</li>
              {deleting.mobile_e164 && <li>Trial-mobile lock on {deleting.mobile_e164} (releases the number)</li>}
            </ul>
          </div>
          <div className="flex flex-col gap-2">
            <label className="text-[12px] text-slate-600">
              Type <span className="font-mono text-rose-700">DELETE</span> to confirm
              <input data-testid="admin-delete-confirm-input" value={delConfirmText}
                onChange={(e) => setDelConfirmText(e.target.value)}
                placeholder="DELETE"
                className="w-full mt-1 bg-white text-slate-800 text-[13px] rounded px-2 py-1.5 outline-none border border-slate-300 font-mono uppercase tracking-widest"/>
            </label>
            <label className="text-[12px] text-slate-600">
              Reason (optional — audit note)
              <input data-testid="admin-delete-note" value={delNote} onChange={(e) => setDelNote(e.target.value)}
                placeholder="Cleanup of test/trial account"
                className="w-full mt-1 bg-white text-slate-800 text-[13px] rounded px-2 py-1.5 outline-none border border-slate-300"/>
            </label>
            <div className="flex gap-2 pt-1">
              <button data-testid="admin-delete-save" onClick={saveDelete}
                disabled={delSaving || delConfirmText.trim().toUpperCase() !== 'DELETE'}
                className="text-[12px] font-bold px-3 py-1.5 rounded-full bg-rose-600 text-white disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-1">
                {delSaving ? <><Loader2 size={13} className="animate-spin"/> Deleting…</> : <><Trash2 size={13}/> Delete account</>}
              </button>
              <button data-testid="admin-delete-cancel" onClick={() => setDeleting(null)}
                className="text-[12px] font-bold px-3 py-1.5 rounded-full bg-slate-200 text-slate-800">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Iter-52.25 — Ad-source cell. Compact chip showing the utm_source /
 * utm_campaign / fbclid at a glance. Click to expand a details block
 * with every captured field (referrer, landing URL, first_touch_at).
 * Attribution is captured once, on first browser visit — so what you
 * see here is the ORIGINAL ad the user clicked before signing up,
 * even if they later revisited via a different link.
 */
function AdSourceCell({ attribution, userId }) {
  const [open, setOpen] = useState(false);
  const a = attribution || {};
  const hasAny = !!(a.utm_source || a.utm_campaign || a.fbclid || a.gclid
                    || a.http_referrer || a.first_landing_url);
  if (!hasAny) {
    return <span className="text-slate-400">—</span>;
  }
  // Prefer utm_source; fall back to fbclid presence or referrer domain.
  let label = a.utm_source;
  if (!label && a.fbclid) label = 'facebook';
  if (!label && a.gclid) label = 'google';
  if (!label && a.http_referrer) {
    try { label = new URL(a.http_referrer).hostname.replace(/^www\./, ''); }
    catch { label = 'referrer'; }
  }
  if (!label) label = 'direct';
  const chipCls =
    /facebook|fb/i.test(label) ? 'bg-blue-100 text-blue-800' :
    /google/i.test(label) ? 'bg-amber-100 text-amber-800' :
    /instagram|ig/i.test(label) ? 'bg-pink-100 text-pink-800' :
    'bg-slate-100 text-slate-800';
  return (
    <div className="relative">
      <button data-testid={`admin-user-adsource-chip-${userId}`}
        onClick={() => setOpen(v => !v)}
        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold uppercase tracking-wide ${chipCls} hover:opacity-80`}
        title="Click to see full attribution">
        {label}
        {a.utm_campaign && <span className="text-[10px] normal-case font-normal">· {a.utm_campaign}</span>}
      </button>
      {open && (
        <div data-testid={`admin-user-adsource-details-${userId}`}
          className="absolute z-30 mt-1 left-0 min-w-[260px] max-w-[380px] bg-white border border-slate-300 rounded-lg shadow-lg p-2 text-[11px] leading-relaxed">
          <div className="font-bold uppercase tracking-wider text-slate-500 mb-1">
            First-touch attribution
          </div>
          <dl className="space-y-0.5">
            {[
              ['utm_source', a.utm_source],
              ['utm_medium', a.utm_medium],
              ['utm_campaign', a.utm_campaign],
              ['utm_term', a.utm_term],
              ['utm_content', a.utm_content],
              ['fbclid', a.fbclid],
              ['gclid', a.gclid],
              ['first_landing_url', a.first_landing_url],
              ['http_referrer', a.http_referrer],
              ['first_touch_at', a.first_touch_at],
            ].filter(([, v]) => !!v).map(([k, v]) => (
              <div key={k} className="flex gap-2 items-start">
                <dt className="font-mono text-slate-500 shrink-0">{k}:</dt>
                <dd className="font-mono text-slate-800 break-all">{v}</dd>
              </div>
            ))}
          </dl>
          <button data-testid={`admin-user-adsource-close-${userId}`}
            onClick={() => setOpen(false)}
            className="mt-1 text-[10px] font-bold text-slate-500 hover:text-slate-800">
            close
          </button>
        </div>
      )}
    </div>
  );
}

// Iter-52.40 — Shared period-filter row used by the three admin date
// filters (Registered / Kid profile added / Zero-activity). Renders
// a master toggle button, an inline segmented preset picker (Today /
// Yesterday / 2d / 3d / 7d / 15d / Custom), and — when Custom is
// picked — a from/to date-picker + Apply button. Keeps the UI shape
// identical across all three filters so admins learn one pattern.
function PeriodFilterRow({
  testIdPrefix, label,
  active, period, from, to,
  onToggle, onPreset, onCustomFromChange, onCustomToChange, onCustomApply,
  busy,
}) {
  return (
    <div className="mb-2 flex items-center gap-2 flex-wrap" data-testid={`${testIdPrefix}-row`}>
      <button
        data-testid={`${testIdPrefix}-toggle`}
        onClick={onToggle}
        className={`text-[12px] font-bold px-2.5 py-1 rounded-full inline-flex items-center gap-1 ${active ? 'bg-indigo-600 text-white hover:bg-indigo-700' : 'bg-slate-200 text-slate-800 hover:bg-slate-300'}`}
        title={`Filter by ${label.toLowerCase()} date`}
      >
        {label}
      </button>
      {active && (
        <>
          <div className="inline-flex bg-slate-100 rounded-full p-0.5 flex-wrap" data-testid={`${testIdPrefix}-period-group`}>
            {[
              ['today',     'Today'],
              ['yesterday', 'Yesterday'],
              ['2d_before', '2d ago'],
              ['3d_before', '3d ago'],
              ['7d',        '7 days'],
              ['15d',       '15 days'],
              ['custom',    'Custom'],
            ].map(([key, l]) => (
              <button
                key={key}
                data-testid={`${testIdPrefix}-period-${key}`}
                onClick={() => onPreset(key)}
                className={`text-[11px] font-bold px-2.5 py-1 rounded-full ${period === key ? 'bg-white text-indigo-700 shadow-sm' : 'text-slate-600 hover:text-slate-800'}`}
              >{l}</button>
            ))}
          </div>
          {period === 'custom' && (
            <div className="inline-flex items-center gap-1" data-testid={`${testIdPrefix}-custom`}>
              <input
                data-testid={`${testIdPrefix}-from`}
                type="date"
                value={from}
                onChange={(e) => onCustomFromChange(e.target.value)}
                className="text-[12px] bg-white border border-slate-300 rounded px-1.5 py-0.5"
              />
              <span className="text-slate-500 text-[12px]">to</span>
              <input
                data-testid={`${testIdPrefix}-to`}
                type="date"
                value={to}
                onChange={(e) => onCustomToChange(e.target.value)}
                className="text-[12px] bg-white border border-slate-300 rounded px-1.5 py-0.5"
              />
              <button
                data-testid={`${testIdPrefix}-custom-apply`}
                onClick={onCustomApply}
                disabled={!from || !to || busy}
                className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-60"
              >Apply</button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

